/* ============================================================
   ZNAKI · index.js
   Spięcie całości: pętla animacji, kursor, parametry URL.

   Publiczne API (używa go HomePlayController):
     initPlayground(container)  — zbuduj scenę i uruchom pętlę
     stopPlayground()           — wstrzymaj pętlę (stan zostaje)
     destroyPlayground()        — zatrzymaj i posprzątaj wszystko

   Dźwięk mieszka w ./audio (tło + silnik dla przyszłych akcentów).
   Kontekst audio rusza dopiero po pierwszym geście użytkownika —
   ekranu wprowadzającego świadomie nie ma.

   Parametry URL (do testów i dzielenia się kompozycją):
     ?seed=ABCDE   — konkretna kompozycja
     ?scroll=2.5   — pozycja startowa [vh]
     ?panel        — panel parametrów otwarty od startu (tylko dev)
     ?debug        — licznik FPS i figur (tylko dev)
   ============================================================ */

import { PALETTE, PARAMS } from './config';
import { clamp01, easeOutPow, hash01, randomSeed } from './utils';
import Renderer from './renderer';
import Composer from './composer';
import ScrollEngine from './scroll';
import Controls from './controls';
import Intro from './intro';
import { evaluate } from './figures';
import {
  initAudio,
  updateAudio,
  pauseAudio,
  resumeAudio,
  destroyAudio,
  getAudioState,
} from './audio';

const IS_DEV = process.env.NODE_ENV === 'development';
/* uwaga: klasa "wj-installation" na <html> jest renderowana po stronie serwera
   (views/__2026/index.php) i jedzie w data-html-classes, więc ASwap podmienia ją
   atomowo razem z treścią. Nie dotykamy jej z JS — inaczej wyścig z
   ASwapDispatcher.swapBodyToHtmlClasses() gasi ją po podmianie widoku. */

const SELECTOR_ROOT = '.editionhome-playground';
const SELECTOR_CANVAS = '.playground-canvas';
const SELECTOR_CURSOR = '.playground-cursor';

export const APP_STATE = {
  initialized: false,
  isPlaying: false,
  seed: null,
  root: null,
  canvas: null,
  cursorEl: null,
  renderer: null,
  composer: null,
  engine: null,
  controls: null,
  intro: null,
  debugEl: null,
};

const mouse = { x: 0, y: 0, sx: 0, sy: 0, px: 0, py: 0 };
let holdAmt = 0;
let flashAmt = 0;
let lastT = 0;
let rafId = null;
let reducedMotion = false;
let finePointer = false;
let frames = 0, fpsT = 0, fps = 60;

/* Wejście instalacji. Do kliknięcia "rozpocznij" rysujemy sam papier —
   figury czekają za sceną. Potem `opening` schodzi 1 → 0 i wnosi je w kadr
   (patrz applyOpening). Bez ekranu wprowadzającego scena stoi od razu. */
let revealed = true;
let opening = 0;
let openT = 0;
const NO_SPECS = [];

/* ============================================================
   API
   ============================================================ */

export const initPlayground = (container) => {
  const root = (container || document).querySelector(SELECTOR_ROOT)
    || document.querySelector(SELECTOR_ROOT);
  if (!root) return false;

  /* ta sama scena już stoi (podwójne wywołanie z cyklu aswap) — tylko wznów */
  if (APP_STATE.initialized && APP_STATE.root === root) {
    if (!APP_STATE.isPlaying) {
      APP_STATE.isPlaying = true;
      resumeAudio();
      lastT = performance.now();
      rafId = requestAnimationFrame(frame);
    }
    return true;
  }
  /* powtórne wejście na widok (aswap) — najpierw sprzątamy */
  if (APP_STATE.initialized) destroyPlayground();

  const canvas = root.querySelector(SELECTOR_CANVAS);
  if (!canvas) return false;

  const qs = new URLSearchParams(window.location.search);
  reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  finePointer = window.matchMedia('(pointer: fine)').matches;

  APP_STATE.root = root;
  APP_STATE.canvas = canvas;
  APP_STATE.cursorEl = root.querySelector(SELECTOR_CURSOR);
  APP_STATE.seed = (qs.get('seed') || randomSeed()).toUpperCase().slice(0, 12);

  APP_STATE.renderer = new Renderer(canvas);
  APP_STATE.composer = new Composer(APP_STATE.seed);
  APP_STATE.engine = new ScrollEngine();

  if (qs.has('scroll')) {
    const s = parseFloat(qs.get('scroll')) || 0;
    APP_STATE.engine.value = APP_STATE.engine.target = s;
  }

  mouse.px = window.innerWidth / 2;
  mouse.py = window.innerHeight / 2;
  mouse.x = mouse.y = mouse.sx = mouse.sy = 0;
  holdAmt = 0;
  flashAmt = 0;

  addEventListeners();
  APP_STATE.engine.attach(root);
  initAudio(root);

  /* ekran wprowadzający, stopka i nakładka "info" leżą obok tablicy,
     w kontenerze widoku — stąd szukanie od .editionhome-full */
  APP_STATE.intro = new Intro(root.closest('.editionhome-full') || document, {
    onStart: () => startOpening(),
  });
  revealed = !APP_STATE.intro.active;
  opening = 0;
  openT = 0;
  setRunning(revealed);

  if (IS_DEV) initDevTools(qs);

  APP_STATE.initialized = true;
  APP_STATE.isPlaying = true;
  lastT = performance.now();
  rafId = requestAnimationFrame(frame);
  return true;
};

export const stopPlayground = () => {
  APP_STATE.isPlaying = false;
  pauseAudio();
  if (rafId) cancelAnimationFrame(rafId);
  rafId = null;
};

export const destroyPlayground = () => {
  stopPlayground();
  setRunning(false);
  removeEventListeners();
  destroyAudio();
  if (APP_STATE.renderer) APP_STATE.renderer.destroy();
  if (APP_STATE.engine) APP_STATE.engine.detach();
  if (APP_STATE.controls) APP_STATE.controls.destroy();
  if (APP_STATE.intro) APP_STATE.intro.destroy();
  if (APP_STATE.debugEl) APP_STATE.debugEl.remove();

  APP_STATE.initialized = false;
  APP_STATE.root = null;
  APP_STATE.canvas = null;
  APP_STATE.cursorEl = null;
  APP_STATE.renderer = null;
  APP_STATE.composer = null;
  APP_STATE.engine = null;
  APP_STATE.controls = null;
  APP_STATE.intro = null;
  APP_STATE.debugEl = null;
  revealed = true;
  opening = 0;
  openT = 0;
};

/* nowa kompozycja (ten sam parametr, inne ziarno) */
export const regenPlayground = (newSeed) => {
  if (!APP_STATE.composer) return false;
  APP_STATE.seed = (newSeed || randomSeed()).toUpperCase();
  APP_STATE.composer.regen(APP_STATE.seed);
  if (APP_STATE.controls) APP_STATE.controls.refresh();
  flashAmt = 1;   /* mgnienie papieru przy nowej kompozycji */
  return APP_STATE.seed;
};

/* ============================================================
   Wejście instalacji
   ============================================================ */

/* Własny kursor (i schowanie natywnego) dopiero od chwili, gdy tablica gra —
   ekran wprowadzający czyta się jak zwykła strona, ze zwykłym kursorem.
   @param {Boolean} on */
const setRunning = (on) => {
  if (APP_STATE.root) APP_STATE.root.classList.toggle('is-running', !!on);
};

/* Kliknięcie "rozpocznij": tablica rusza, a figury wjeżdżają zza sceny.
   Kliknięcie jest zarazem gestem odblokowującym dźwięk (audio/engine.js). */
const startOpening = () => {
  if (revealed) return;
  revealed = true;
  opening = 1;
  openT = 0;
  setRunning(true);
  lastT = performance.now();          /* bez tego pierwsza klatka dostaje cały postój */
  if (APP_STATE.engine) {
    APP_STATE.engine.startIntro(PARAMS.intro.startDist, PARAMS.intro.startDur);
  }
};

/* Wnosi figury w kadr: każda startuje spod dolnej krawędzi, we własnej
   chwili (rozstrzelenie z ziarna figury) — ciężkie prowadzą, lekkie
   dosypują się za nimi. Zwraca listę figur już obecnych na scenie:
   te, które jeszcze czekają, nie rysują się i nie brzmią.
   @param {Array} specs — specyfikacje z evaluate()
   @param {Number} q — postęp wejścia 0 → 1
   @param {Object} st — stan chwili
   @return {Array} */
function applyOpening(specs, q, st) {
  const dist = PARAMS.intro.figDist * st.vhPx;
  const out = [];
  for (const s of specs) {
    const f = s.fig;
    if (f._openD === undefined) {
      const heavy = f.kind === 'heavy';
      f._openD = (heavy ? 0.02 : 0.14) + hash01(f.seed) * (heavy ? 0.22 : 0.44);
    }
    const t = clamp01((q - f._openD) / Math.max(0.08, 1 - f._openD));
    if (t <= 0) continue;
    const e = easeOutPow(t, 3.4);
    s.y += (1 - e) * dist;
    s.rot += (1 - e) * (hash01(f.seed + 7) - 0.5) * 0.5;
    s.alpha *= clamp01(t / 0.25);
    if (s.alpha > 0.008) out.push(s);
  }
  return out;
}

/* ============================================================
   Nasłuchy
   ============================================================ */

const addEventListeners = () => {
  window.addEventListener('resize', onWindowResize);
  window.addEventListener('pointermove', onPointerMove);
};

const removeEventListeners = () => {
  window.removeEventListener('resize', onWindowResize);
  window.removeEventListener('pointermove', onPointerMove);
};

const onWindowResize = () => {
  if (APP_STATE.renderer) APP_STATE.renderer.resize();
};

const onPointerMove = (e) => {
  mouse.px = e.clientX; mouse.py = e.clientY;
  mouse.x = (e.clientX / window.innerWidth) * 2 - 1;
  mouse.y = (e.clientY / window.innerHeight) * 2 - 1;
  if (finePointer && APP_STATE.cursorEl) {
    const overUI = e.target.closest && e.target.closest(
      'header, footer, #box-header, #box-footer, #box-global-search, #wj-playground-panel, a, button, input'
    );
    APP_STATE.cursorEl.classList.toggle('is-hidden', !!overUI);
  }
};

/* ============================================================
   Pętla
   ============================================================ */

function frame(now) {
  if (!APP_STATE.isPlaying) return;
  rafId = requestAnimationFrame(frame);

  const { engine, renderer, composer, cursorEl } = APP_STATE;
  const dt = Math.min(0.05, (now - lastT) / 1000);
  lastT = now;

  /* przed startem tablica stoi — inaczej autodryf odjechałby pod ekranem */
  if (revealed) engine.update(dt, reducedMotion);

  /* skupienie (przytrzymanie) — łagodne dojście */
  holdAmt += ((engine.holdTarget ? 1 : 0) - holdAmt) * Math.min(1, dt * 5);

  /* wygładzony kursor */
  mouse.sx += (mouse.x - mouse.sx) * Math.min(1, dt * 4);
  mouse.sy += (mouse.y - mouse.sy) * Math.min(1, dt * 4);
  if (finePointer && cursorEl) {
    cursorEl.classList.toggle('is-hold', engine.holdTarget === 1);
    cursorEl.style.transform =
      'translate3d(' + mouse.px + 'px,' + mouse.py + 'px,0) translate(-50%,-50%)';
  }

  /* stan chwili dla ewaluacji figur */
  const st = {
    scroll: engine.value,
    velSmooth: engine.velSmooth,
    velMag: clamp01(Math.abs(engine.velSmooth) / 1.6),
    t: now / 1000,
    dt,
    now,
    hold: holdAmt,
    mx: mouse.sx, my: mouse.sy,
    vwPx: renderer.w, vhPx: renderer.h,
    vminPx: Math.min(renderer.w, renderer.h),
    cx: renderer.w / 2, cy: renderer.h / 2,
    reduced: reducedMotion,
  };

  /* ekran wprowadzający: sam papier — figury czekają za sceną */
  if (!revealed) {
    renderer.render(NO_SPECS, st);
    updateAudio(st, dt, NO_SPECS);
    return;
  }

  /* zbierz i oceń figury */
  const figs = composer.collect(st.scroll);
  let specs = [];
  for (const f of figs) {
    const s = evaluate(f, st);
    if (s) specs.push(s);
  }

  /* wejście: figury wnoszą się w kadr spod dolnej krawędzi */
  if (opening > 0) {
    openT += dt;
    opening = 1 - clamp01(openT / Math.max(0.2, PARAMS.intro.startDur));
    specs = applyOpening(specs, 1 - opening, st);
  }

  renderer.render(specs, st);

  updateAudio(st, dt, specs);

  /* mgnienie po regeneracji */
  if (flashAmt > 0.01) {
    const ctx = renderer.ctx;
    ctx.setTransform(renderer.dpr, 0, 0, renderer.dpr, 0, 0);
    ctx.globalAlpha = flashAmt * 0.85;
    ctx.fillStyle = PALETTE.bg;
    ctx.fillRect(0, 0, renderer.w, renderer.h);
    ctx.globalAlpha = 1;
    flashAmt *= Math.exp(-dt * 5);
  }

  if (APP_STATE.debugEl) updateDebug(now, specs.length);
}

/* ============================================================
   Narzędzia deweloperskie (tylko build development)
   ============================================================ */

function initDevTools(qs) {
  /* panel parametrów — budowany dopiero tutaj, nigdy w buildzie produkcyjnym */
  APP_STATE.controls = new Controls({
    onReseed: () => regenPlayground(),
    getSeed: () => APP_STATE.seed,
  });
  if (qs.has('panel')) APP_STATE.controls.toggle(true);
  document.addEventListener('keydown', onDevKeyDown);

  if (qs.has('debug')) {
    const el = document.createElement('div');
    el.style.cssText =
      'position:fixed;left:16px;top:50%;z-index:1001;font:10px/1.6 monospace;' +
      'color:#171207;background:rgba(235,223,195,.8);padding:6px 8px;pointer-events:none';
    document.body.appendChild(el);
    APP_STATE.debugEl = el;
  }
}

function onDevKeyDown(e) {
  if (!APP_STATE.controls) {
    document.removeEventListener('keydown', onDevKeyDown);
    return;
  }
  if (e.target && /^(INPUT|TEXTAREA|SELECT)$/.test(e.target.tagName)) return;
  const k = e.key.toLowerCase();
  if (k === 'c') APP_STATE.controls.toggle();
  else if (k === 'escape') APP_STATE.controls.toggle(false);
}

function updateDebug(now, figCount) {
  frames++;
  if (now - fpsT > 500) {
    fps = Math.round(frames * 1000 / (now - fpsT));
    frames = 0; fpsT = now;
  }
  const snd = getAudioState();
  APP_STATE.debugEl.textContent =
    fps + ' fps · fig ' + figCount +
    ' · scroll ' + APP_STATE.engine.value.toFixed(2) +
    ' · ciężkie w kadrze ' + APP_STATE.composer.visibleHeavyCount(APP_STATE.engine.value) +
    ' · ziarno ' + APP_STATE.seed +
    ' · glitch ' + APP_STATE.renderer.glitchBackend() +
    (snd
      ? ' · tło ' + (snd.running ? 'tlo_' + (snd.index + 1) : 'czeka na gest') +
        ' ' + snd.level.toFixed(2) + (snd.pending ? ' →' : '') +
        ' · ziarna ' + snd.grains + '/' + snd.figs +
        ' zeg ' + snd.clocks + ' fale ' + snd.waves
      : '');
}
